import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
    UserSwitchOutlined,
    TransactionOutlined,
    BankOutlined,
    BarChartOutlined,
    SettingOutlined,
    NotificationOutlined,
    ProductOutlined,
    ShareAltOutlined,
    DownOutlined,
    UserDeleteOutlined,
    DeliveredProcedureOutlined
} from '@ant-design/icons';
import { Layout, Menu, Button, Dropdown, Space, Flex, Typography } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { connect } from "react-redux";
import { LocalStorageService } from '../services/LocalStorage';
import { isLogin, setUserData } from '../actions';
import Notification from './Notification';
import AxiosService from '../services/APIService';
import webSocketService from '../services/websocketService';
import {getLoginRoute} from '../utils/commonFunc';

const { Header, Sider, Content } = Layout;

const menuItems = [
    { key: '1', icon: <ProductOutlined />, label: 'Dashboard', path: '/wl/home' },
    { key: '2', icon: <UserOutlined />, label: 'Users', path: '/wl/users' },
    { key: '3', icon: <UserSwitchOutlined />, label: 'Pending Users', path: '/wl/pending-users' },
    { key: '4', icon: <UserDeleteOutlined />, label: 'Closed Users', path: '/wl/close-user' },
    {
        key: '5', icon: <TransactionOutlined />, label: 'Transaction', children: [
            { key: '5-1', label: 'Deposit Report', path: '/wl/deposit-report' },
            { key: '5-2', label: 'Withdraw Report', path: '/wl/withdraw-report' },
            { key: '5-3', label: 'Bot Charge', path: '/wl/botcharge-report' }
        ]
    },
    { key: '9', icon: <NotificationOutlined />, label: 'Pay Bill', path: '/wl/my-bill' },
    { key: '10', icon: <BankOutlined />, label: 'Brokers', path: '/wl/broker' },
    { key: '6', icon: <BarChartOutlined />, label: 'Profit', path: '/wl/profit' },
    {
        key: '7', icon: <SettingOutlined />, label: 'Service Request', children: [
            { key: '7-1', label: 'Note Request', path: '/wl/note-request' },
            { key: '7-2', label: 'Setting Request', path: '/wl/setting-request' },
        ]
    },
    { key: '8', icon: <NotificationOutlined />, label: 'Announcement', path: '/wl/announcement' },
    { key: '11', icon: <UserSwitchOutlined />, label: 'My Profile', path: '/wl/my-profile' },
    { key: '12', icon: <ShareAltOutlined />, label: 'Inquiries', path: '/wl/inquiry' },
    { key: '13', icon: <ShareAltOutlined />, label: 'Exhibition Inquiries', path: '/wl/exhibition' },
    { key: '14', icon: <DeliveredProcedureOutlined />, label: 'Email Info', path: '/wl/email-info' }
];

const WLLayout = ({ children, user, isLogin, setUserData, customer, currentRoute, whiteLabel }) => {
    const [currentActive, setCurrentActive] = useState('1');
    const [collapsed, setCollapsed] = useState(false);
    const [userCount, setUserCount] = useState({
        pendingUser: 0,
        user: 0,
        closeUser: 0,
        editReq: 0,
        settingReq: 0
    });

    const location = useLocation();
    const searchParams = location.search;
    const navigate = useNavigate();
    const apiService = new AxiosService();

    const activeLink = useCallback((route) => {
        const routeMap = {
            "/wl/home": "1",
            "/wl/users": "2",
            "/wl/pending-users": "3",
            "/wl/close-user": "4",
            "/wl/deposit-report": "5-1",
            "/wl/withdraw-report": "5-2",
            "/wl/botcharge-report": "5-3",
            "/wl/profit": "6",
            "/wl/note-request": "7-1",
            "/wl/setting-request": "7-2",
            "/wl/announcement": "8",
            "/wl/my-bill": "9",
            "/wl/broker": "10",
            "/wl/my-profile": "11",
            "/wl/inquiry": "12",
            "/wl/exhibition": "13",
            "/wl/email-info": "14"
        };
        setCurrentActive(routeMap[route] || '1');
    }, []);

    useEffect(() => {
        activeLink(currentRoute);
    }, [currentRoute, activeLink]);

    const fetchCounters = useCallback(() => {
        if(user?._id) {
            apiService.get(`/wl-counter/${user?._id}`)
                .then(res => {
                    setUserCount({
                        closeUser: res.closedUsers,
                        pendingUser: res.pendingUsers,
                        user: res.users,
                        settingReq: res.settingReq,
                        editReq: res.editReq,
                    });
                })
                .catch(console.error);
        }
    }, [user]);

    useEffect(() => {
        webSocketService.onMessage('counterHit', fetchCounters);
        fetchCounters();
    }, [fetchCounters]);

    const logoutHandle = useCallback(() => {
        LocalStorageService.removeItem("userToken");
        LocalStorageService.removeItem("user");
        setUserData(null);
        isLogin(false);
        navigate(getLoginRoute({...user, ...customer}))
    }, [setUserData, isLogin, navigate]);

    const items = useMemo(() => [
        { label: "Logout", key: '0', onClick: logoutHandle }
    ], [logoutHandle]);

    const menuClick = useCallback((val) => {
        if (val.key === "0") logoutHandle();
    }, [logoutHandle]);

    const headerStyle = useMemo(() => ({
        padding: '0PX 20PX',
        background: "#131c2b",
    }), []);

    const contentStyle = useMemo(() => ({
        margin: '24px 16px',
        padding: 24,
        background: "rgb(220 220 220 / 52%)",
        borderRadius: 8,
        minHeight: "1000px"
    }), []);

    return (
        <Layout style={{ height: "100%" }}>
            <Sider
                theme='dark'
                trigger={null}
                collapsible
                collapsed={collapsed}
                width={230}
                style={{ background: "#131c2b" }}
            >
                <div className="demo-logo-vertical sidebar-logo">
                    <img src={`${process.env.REACT_APP_API_URL}white-label/logo/${user?._id}`} style={{ width: collapsed ? "auto" : "100%", height: collapsed ? "40px" : "auto" }} alt="logo" />
                </div>
                <Menu
                    mode="inline"
                    theme='dark'
                    selectedKeys={[currentActive]}
                    items={menuItems.map(item => ({
                        ...item,
                        label: item.children ? item.label : <Link to={`${item.path}${searchParams}`}>{item.label}</Link>,
                        children: item.children?.map(child => ({
                            ...child,
                            label: <Link to={`${child.path}${searchParams}`}>{child.label}</Link>
                        }))
                    }))}
                    defaultOpenKeys={['4', '9']}
                    style={{ background: "#131c2b" }}
                />
            </Sider>
            <Layout>
                <Header style={headerStyle}>
                    <Flex justify='space-between'>
                        <Space size={'large'}>
                            <Button
                                type="text"
                                icon={collapsed ? <MenuUnfoldOutlined style={{ color: "#fff" }} /> : <MenuFoldOutlined style={{ color: "#fff" }} />}
                                onClick={() => setCollapsed(!collapsed)}
                                style={{ fontSize: '16px', width: 64, height: 64 }}
                            />
                        </Space>
                        <Space size={20}>
                            <Typography.Text style={{ color: "#fff" }}>Level {whiteLabel?.level}</Typography.Text>
                            <Notification />
                            <Dropdown menu={{ items, onClick: menuClick }} trigger={['click']}>
                                <a onClick={(e) => e.preventDefault()}>
                                    <Space style={{ color: "#fff" }} className="user-name">
                                        {user?.fullName}
                                        <DownOutlined />
                                    </Space>
                                </a>
                            </Dropdown>
                        </Space>
                    </Flex>
                </Header>
                <Content style={contentStyle}>
                    {children}
                </Content>
            </Layout>
        </Layout>
    );
};

const mapStateToProps = ({ login, url }) => ({
    user: login.user,
    customer: login.customer,
    currentRoute: url.currentRoute,
    whiteLabel: login.whiteLabel
});

const mapDispatchToProps = {
    isLogin,
    setUserData
};

export default connect(mapStateToProps, mapDispatchToProps)(WLLayout);